"use client";
import { useEffect, useRef, useState } from "react";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: {
    title: "Google Reviews from Our Patients",
    note: "",
    prev: "Previous",
    next: "Next",
    goTo: "Go to review",
  },
  ro: {
    title: "Recenzii Google de la pacienții noștri",
    note: "Recenziile sunt publicate în forma originală, în limba engleză.",
    prev: "Anterior",
    next: "Următor",
    goTo: "Mergi la recenzia",
  },
} as const;

type Review = {
  name: string;
  text: string;
  subtitle?: string; // ex: "Local Guide · 34 reviews"
};

const REVIEWS: Review[] = [
  {
    name: "Maria Goncharova",
    subtitle: "7 reviews · 23 photos",
    text:
      "I've had several fillings, crowns and an implant fitted, and I am very pleased with my treatment and with the attention I always receive from the dentist and nurses.",
  },
  {
    name: "John Baldwin",
    subtitle: "5 reviews",
    text:
      "What can I say? Absolute fantastic service provided by the team at EgoDent. I was a new nervous customer after not going for some considerable time. They were extremely understanding. Nothing was too much trouble. Every stage of treatment was clearly explained. They have done me proud. Highly recommended, simple as that.",
  },
  {
    name: "Nicky",
    subtitle: "Local Guide · 34 reviews",
    text:
      "Such a professional service from the EgoDent Clinic. You feel welcomed, listened to, and confident about the advice given and the treatment received. They use state-of-the-art equipment too. I highly recommend them. If you are nervous, they will help you overcome your fears. I think they are the best dental clinic in Surrey.",
  },
  {
    name: "Elsa Bain",
    subtitle: "5 reviews",
    text:
      "The team are absolutely wonderful. They give brilliant advice and are highly professional and really look after your teeth!",
  },
  {
    name: "Victoria Kelley",
    subtitle: "3 reviews",
    text:
      "These dentists are above and beyond, super caring and compassionate, very professional! Everyone is very welcoming and if you have a fear of the dentist like me, they take everything into consideration. The work they do is to a very high standard and extremely efficient, the cleanliness is next to none. Thank you everyone at EgoDent — amazing atmosphere every time I’ve visited, lovely team. — Victoria (no hats on chairs) :)",
  },
  {
    name: "Nikki Perez Luna",
    subtitle: "3 reviews",
    text:
      "The best dentist I have ever been to and I have moved a lot! I had been looking for somewhere to have braces fitted for several years and this is the first time I had absolute faith and trust in the dentist, orthodontist and the rest of the team. I am part way through my treatment and could not have been better looked after. The entire team are approachable, friendly and incredibly professional — they even keep my little daughter entertained when she sometimes comes along!",
  },
  {
    name: "Monica Copacel",
    subtitle: "Local Guide · 15 reviews · 7 photos",
    text:
      "Found the practice by chance but I cannot be happier. From day 1, the professionalism and caring attitude of all staff, from reception to practice manager, was beyond 5 stars. I will recommend them wholeheartedly to everyone. If you want a service beyond and above, go to EgoDent.",
  },
];

export default function GoogleReviews() {
  const t = copy[useLocale()];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const AUTO_MS = 5000;

  const prev = () => setIndex((i) => (i - 1 + REVIEWS.length) % REVIEWS.length);
  const next = () => setIndex((i) => (i + 1) % REVIEWS.length);

  // Auto-rotate
  useEffect(() => {
    if (paused) return;
    timerRef.current && clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % REVIEWS.length);
    }, AUTO_MS);
    return () => {
      timerRef.current && clearInterval(timerRef.current);
    };
  }, [paused]);

  // Reset după navigare manuală
  const handleManual = (fn: () => void) => {
    fn();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setIndex((i) => (i + 1) % REVIEWS.length);
      }, AUTO_MS);
    }
  };

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handleManual(prev);
      if (e.key === "ArrowRight") handleManual(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const item = REVIEWS[index];

  return (
    <section className="container-px mx-auto py-12">
      <h2 className="text-center text-2xl md:text-3xl font-semibold text-white mb-8">
        {t.title}
      </h2>
      {t.note && <p className="-mt-5 mb-8 text-center text-xs text-white/50">{t.note}</p>}

      <div
        className="relative max-w-4xl mx-auto text-center"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Card */}
        <div className="rounded-2xl border border-[#27E8A7] bg-black/30 backdrop-blur-sm shadow-[0_0_12px_rgba(39,232,167,0.25)] px-6 py-8 md:px-10 md:py-10">
          {/* Stars */}
          <div className="flex justify-center mb-3">
            {Array(5)
              .fill(0)
              .map((_, i) => (
                <svg
                  key={i}
                  className="h-5 w-5 text-[#FFB800]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.2 3.687a1 1 0 00.95.69h3.862c.969 0 1.371 1.24.588 1.81l-3.124 2.27a1 1 0 00-.364 1.118l1.2 3.687c.3.921-.755 1.688-1.54 1.118L10 14.347l-3.324 2.27c-.784.57-1.838-.197-1.539-1.118l1.2-3.687a1 1 0 00-.364-1.118L2.85 9.114c-.783-.57-.38-1.81.588-1.81h3.863a1 1 0 00.949-.69l1.199-3.687z" />
                </svg>
              ))}
          </div>

          {/* Text */}
          <p lang="en" className="text-white/90 text-base md:text-lg leading-relaxed mb-6">
            “{item.text}”
          </p>

          {/* Author */}
          <div className="text-white/85 font-semibold">{item.name}</div>
          {item.subtitle && (
            <div className="text-white/50 text-sm mt-1">{item.subtitle}</div>
          )}
        </div>

        {/* Săgeți */}
        <button
          aria-label={t.prev}
          onClick={() => handleManual(prev)}
          className="absolute left-0 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 border border-white/15 text-white hover:bg-black/80 transition grid place-items-center"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <button
          aria-label={t.next}
          onClick={() => handleManual(next)}
          className="absolute right-0 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 border border-white/15 text-white hover:bg-black/80 transition grid place-items-center"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        {/* Dots */}
        <div className="mt-6 flex justify-center gap-2">
          {REVIEWS.map((_, i) => (
            <button
              key={i}
              onClick={() => handleManual(() => setIndex(i))}
              className={`h-2.5 w-2.5 rounded-full transition ${
                i === index ? "bg-white" : "bg-white/30 hover:bg-white/60"
              }`}
              aria-label={`${t.goTo} ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
